import { resizeSection } from './sets';
import type { SetLog } from '../../shared/types';

/** How a logged number compares with the last time this exercise was done. */
export type Trend = 'up' | 'down' | 'same' | null;

/**
 * Compares a value with the previous session's value for the same set.
 * Returns null when either side is missing — there is nothing to say about a
 * number that has no counterpart.
 */
export function compareToPrevious(
  current: number | null | undefined,
  previous: number | null | undefined,
): Trend {
  if (current == null || previous == null) return null;
  if (current > previous) return 'up';
  if (current < previous) return 'down';
  return 'same';
}

/** The previous session's set at the same position within its own kind. */
export function previousSetFor(
  set: SetLog,
  sets: SetLog[],
  previousSets: SetLog[],
): SetLog | null {
  const sameKind = (s: SetLog) => s.warmup === set.warmup;
  const position = sets.filter(sameKind).findIndex(s => s.setNumber === set.setNumber);
  if (position < 0) return null;
  const previousOfKind = previousSets.filter(sameKind);
  return previousOfKind[position] ?? null;
}

interface Bounds {
  warmupMin?: number;
  warmupMax?: number;
}

/** A set the user has not touched, so autofill may safely populate it. */
const untouched = (s: SetLog) => !s.completed && s.weight == null && s.reps == null;

/**
 * Pre-fills an exercise from the last time it was performed: the same number of
 * warm-up sets (clamped to what the program allows), and each set's weight and
 * reps carried across positionally.
 *
 * Only ever writes into untouched sets, so it cannot overwrite anything logged
 * this session or anything already typed. Returns the original array when there
 * is nothing to fill — the caller runs this from an effect, and a fresh array
 * every time would loop forever.
 */
export function autofillFromPrevious(
  sets: SetLog[],
  previousSets: SetLog[],
  bounds: Bounds = {},
): SetLog[] {
  if (previousSets.length === 0) return sets;
  // Never seed from a session where nothing was actually logged.
  if (!previousSets.some(s => s.weight != null || s.reps != null)) return sets;
  // Anything already in progress is left exactly as it is.
  if (!sets.every(untouched)) return sets;

  let next = sets;

  // Match the warm-up count the user actually did last time, within bounds.
  const previousWarmups = previousSets.filter(s => s.warmup).length;
  const min = bounds.warmupMin ?? previousWarmups;
  const max = bounds.warmupMax ?? previousWarmups;
  const target = Math.min(Math.max(previousWarmups, min), max);
  if (target !== sets.filter(s => s.warmup).length) {
    next = resizeSection(next, true, target);
  }

  const filled = next.map(s => {
    const prev = previousSetFor(s, next, previousSets);
    if (!prev || (prev.weight == null && prev.reps == null)) return s;
    return { ...s, weight: prev.weight, reps: prev.reps };
  });

  // If the map changed nothing and the resize was a no-op, keep the identity.
  const changed = next !== sets || filled.some((s, i) => s !== next[i]);
  return changed ? filled : sets;
}
